import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { SavedFilter } from '@app/features/v3ndor/core/models/saved-filter.model';
import { SavedFilterChipsComponent } from '@app/features/v3ndor/components/saved-filter-chips/saved-filter-chips.component';
import {
  PERIODO_LABELS,
  PERIODO_PADRAO,
  PeriodoAtalho,
  TicketLogFilterParams,
  resolvePeriodo,
} from '../ticket-log-filter-params';

interface FilterChip {
  key: keyof TicketLogFilterParams;
  label: string;
  value: string;
  removable: boolean;
}

/** Só os dois atalhos dinâmicos aparecem como botão; `custom` não é acionável daqui. */
const ATALHOS: PeriodoAtalho[] = ['week', 'month'];

/**
 * Barra de filtros ativos da tela de histórico de apontamentos, no padrão novo
 * do v3ndor. Cópia enxuta do molde já em produção em
 * `modules/support/support-ticket-active-filters/` — o precedente de usar este
 * padrão dentro de um NgModule legado.
 *
 * Não é o `app-active-filters` do v3ndor porque aquele é tipado em
 * `TicketFilterParams` (situação, sistema, status, tags — nada a ver com esta
 * tela) e injeta `TicketService` só para resolver o nome de cada chip. Aqui os
 * rótulos já vêm no próprio filtro, então o componente não tem dependência
 * nenhuma.
 */
@Component({
  selector: 'app-ticket-log-active-filters',
  standalone: true,
  imports: [ButtonModule, ChipModule, SavedFilterChipsComponent],
  templateUrl: './ticket-log-active-filters.component.html',
  styleUrls: ['./ticket-log-active-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketLogActiveFiltersComponent {
  @Input() set filters(value: TicketLogFilterParams) {
    this._filters = value ?? {};
    this.buildChips();
  }
  get filters(): TicketLogFilterParams {
    return this._filters;
  }

  @Input() enableSavedFilters = false;
  @Input() savedFilters: SavedFilter<TicketLogFilterParams>[] = [];
  @Input() activeSavedFilterId: string | null = null;

  @Output() editFilters = new EventEmitter<void>();
  @Output() removeFilter = new EventEmitter<TicketLogFilterParams>();
  @Output() periodChange = new EventEmitter<PeriodoAtalho>();
  @Output() applySavedFilter = new EventEmitter<SavedFilter<TicketLogFilterParams>>();
  @Output() deactivateSavedFilter = new EventEmitter<void>();
  @Output() deleteSavedFilter = new EventEmitter<SavedFilter<TicketLogFilterParams>>();

  readonly atalhos = ATALHOS;
  readonly periodoLabels = PERIODO_LABELS;

  chips: FilterChip[] = [];
  private _filters: TicketLogFilterParams = {};

  get periodoAtivo(): PeriodoAtalho {
    return this._filters.period ?? PERIODO_PADRAO;
  }

  isAtalhoAtivo(atalho: PeriodoAtalho): boolean {
    return this.periodoAtivo === atalho;
  }

  onAtalhoClick(atalho: PeriodoAtalho): void {
    if (this.isAtalhoAtivo(atalho)) return;
    this.periodChange.emit(atalho);
  }

  private buildChips(): void {
    const f = this._filters;
    this.chips = [];

    if (f.user_ids?.length) {
      this.chips.push({
        key: 'user_ids',
        label: f.user_ids.length === 1 ? 'Usuário' : 'Usuários',
        value: this.resumirLista(f.user_labels, f.user_ids.length),
        // Usuário é obrigatório: remover pelo chip deixaria a tela num estado
        // que a busca recusa, e o usuário não teria como saber por quê.
        removable: false,
      });
    }

    if (f.customer_ids?.length) {
      this.chips.push({
        key: 'customer_ids',
        label: f.customer_ids.length === 1 ? 'Cliente' : 'Clientes',
        value: this.resumirLista(f.customer_labels, f.customer_ids.length),
        removable: true,
      });
    }

    // O chip mostra o rótulo E as datas resolvidas mesmo com atalho ativo: sem
    // elas, "Esta semana" não diz QUAL semana está sendo consultada.
    const periodo = resolvePeriodo(f, new Date());
    if (periodo) {
      const intervalo = `${this.formatarData(periodo.from)} a ${this.formatarData(periodo.to)}`;
      this.chips.push({
        key: 'period',
        label: PERIODO_LABELS[this.periodoAtivo],
        value: intervalo,
        removable: false,
      });
    }
  }

  /** Dois nomes cabem no chip; acima disso vira "Fulano +3". */
  private resumirLista(labels: string[] | undefined, total: number): string {
    if (!labels?.length) return `${total} selecionado(s)`;
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
  }

  private formatarData(iso: string): string {
    const [ano, mes, dia] = iso.split('-');
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : iso;
  }

  onRemoveChip(chip: FilterChip): void {
    const atualizado: TicketLogFilterParams = { ...this._filters };
    if (chip.key === 'customer_ids') {
      delete atualizado.customer_ids;
      delete atualizado.customer_labels;
    }
    this.removeFilter.emit(atualizado);
  }

  onEditFilters(): void {
    this.editFilters.emit();
  }

  // O `SavedFilterChipsComponent` é genérico em `SavedFilter<unknown>` para servir
  // qualquer listagem; o cast devolve o tipo desta tela na fronteira.
  onApplySavedFilter(filter: SavedFilter<unknown>): void {
    this.applySavedFilter.emit(filter as SavedFilter<TicketLogFilterParams>);
  }

  onDeleteSavedFilter(filter: SavedFilter<unknown>): void {
    this.deleteSavedFilter.emit(filter as SavedFilter<TicketLogFilterParams>);
  }
}
